// models
import { DatasetFiltersModel } from 'models/dataset-filters.model';
import { VariantWithLabel } from 'models/variant.model';

// Labels on at least one alternate of every component in a single component
// type. Returns null when the group is exempt: one component, or no alternate
// in the group is labelled.
export const getSharedLabelIds = (
    variants: VariantWithLabel[],
): number[] | null => {
    const labelsByComponent: Record<string, Set<number>> = {};
    variants.forEach((v) => {
        labelsByComponent[v.component_id] ||= new Set();
        if (v.label?.id != null) {
            labelsByComponent[v.component_id].add(v.label.id);
        }
    });

    const labelSets = Object.values(labelsByComponent);
    if (labelSets.length < 2 || labelSets.every((set) => set.size === 0)) {
        return null;
    }

    return [...labelSets[0]].filter((id) =>
        labelSets.every((set) => set.has(id)),
    );
};

export interface LabelSyncWarnings {
    // Keyed by variant_type: true when no message can be generated for that type
    warnings: Record<string, boolean>;
    // Alternates that can never be picked because their label isn't shared
    unreachableAlternateIds: string[];
}

// Mirrors the backend label-sync rule (composer-backend app/channels/label_sync_v2.py)
export const showWarningIconInEditor = (
    data: VariantWithLabel[],
    isLabeledMatchingEnabled: boolean,
): LabelSyncWarnings => {
    const result: LabelSyncWarnings = {
        warnings: {},
        unreachableAlternateIds: [],
    };
    if (!isLabeledMatchingEnabled) {
        return result;
    }

    const groupedByType: Record<string, VariantWithLabel[]> = {};
    data.forEach((item) => {
        (groupedByType[item.component_type_id] ||= []).push(item);
    });

    Object.values(groupedByType).forEach((variants) => {
        const variantType = variants[0].variant_type;
        const sharedLabels = getSharedLabelIds(variants);
        const hasError = sharedLabels !== null && sharedLabels.length === 0;

        result.warnings[variantType] =
            (result.warnings[variantType] ?? false) || hasError;

        if (sharedLabels?.length) {
            variants.forEach((v) => {
                if (v.label?.id == null || !sharedLabels.includes(v.label.id)) {
                    result.unreachableAlternateIds.push(v.id);
                }
            });
        }
    });

    return result;
};

export function normalizeEmptyDatasetFilters(
    data: DatasetFiltersModel,
): DatasetFiltersModel {
    if (data?.filters) {
        const filters = data.filters;
        const allEmpty = Object.values(filters).every(
            (val) => Array.isArray(val) && val.length === 0,
        );

        if (allEmpty) {
            data.filters = {};
        }
    }
    return data;
}

export const getDuplicateComponentMutualLabelMap = (
    variants: VariantWithLabel[],
): Record<string, number[]> => {
    const typeToVariants: Record<string, VariantWithLabel[]> = {};

    variants.forEach((v) => {
        (typeToVariants[v.component_type_id] ||= []).push(v);
    });

    const duplicateMap: Record<string, number[]> = {};

    Object.entries(typeToVariants).forEach(([typeId, comps]) => {
        const sharedLabels = getSharedLabelIds(comps);
        if (sharedLabels !== null) {
            duplicateMap[typeId] = sharedLabels;
        }
    });

    return duplicateMap;
};
